import React from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { RootState } from "../../../store/Index";
import { getRateAmount, IRateCardProps } from "../types/rate-card";
import {
  CalendarMonth,
  Event,
  HourglassBottom,
  Paid,
  Payment,
  ReceiptLong,
  Stream,
} from "@mui/icons-material";

interface ViewRateCardDialogProps {
  onClose: () => void;
  open: boolean;
  selectedRateCard: IRateCardProps | null;
}

export const ViewRateCardDialog: React.FC<ViewRateCardDialogProps> = ({
  onClose,
  open,
  selectedRateCard,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
          {`Viewing The ${selectedRateCard?.role?.name ?? "Selected"} Rate Card`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {!selectedRateCard ? (
          <Alert severity="warning">Could not load the selected rate card.</Alert>
        ) : (
          <>
            <Box display={"flex"} alignItems={"center"} sx={{ mb: 1 }} gap={1}>
              <Typography variant={"body1"} sx={{ fontWeight: 600 }}>
                {selectedRateCard.role?.name ?? "N/A"}
              </Typography>
              <Chip
                label={`ID: ${selectedRateCard.role?.id ?? "N/A"}`}
                size={"small"}
                sx={{
                  bgcolor: "transparent",
                  border: "1px solid",
                  borderColor: "#724E41",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "#724E41",
                  borderRadius: 1,
                }}
              />
            </Box>

            <Box display={"flex"} alignItems={"center"} sx={{ mb: 1 }} gap={1}>
              <Typography variant={"body2"}>{"Role Description:"}</Typography>
              <Typography variant={"body2"}>
                {selectedRateCard.role?.description ?? "N/A"}
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box display={"flex"} alignItems={"center"} sx={{ borderBottom: "1px dotted", py: 0.8 }} justifyContent={"space-between"}>
                <Box display={"flex"} alignItems={"center"} gap={0.8}>
                  <CalendarMonth sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={"body2"}>Effective Date</Typography>
                </Box>

                <Box display={"flex"} sx={{ textAlign: "right" }} flexDirection={"column"}>
                  <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                    {format(new Date(selectedRateCard.effective_date), "dd MMM, yyyy")}
                  </Typography>
                </Box>
              </Box>

              <Box display={"flex"} alignItems={"center"} sx={{ borderBottom: "1px dotted", py: 0.8 }} justifyContent={"space-between"}>
                <Box display={"flex"} alignItems={"center"} gap={0.8}>
                  <Paid sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={"body2"}>Currency</Typography>
                </Box>

                <Box display={"flex"} sx={{ textAlign: "right" }} flexDirection={"column"}>
                  <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                    {selectedRateCard.currency ?? "USD"}
                  </Typography>
                </Box>
              </Box>

              <Box display={"flex"} alignItems={"center"} sx={{ borderBottom: "1px dotted", py: 0.8 }} justifyContent={"space-between"}>
                <Box display={"flex"} alignItems={"center"} gap={0.8}>
                  <HourglassBottom sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={"body2"}>Hourly Rate Amount</Typography>
                </Box>

                <Box display={"flex"} sx={{ textAlign: "right" }} flexDirection={"column"}>
                  <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                    {`$${getRateAmount(selectedRateCard.rate, "hourly")}`}
                  </Typography>
                </Box>
              </Box>

              <Box display={"flex"} alignItems={"center"} sx={{ borderBottom: "1px dotted", py: 0.8 }} justifyContent={"space-between"}>
                <Box display={"flex"} alignItems={"center"} gap={0.8}>
                  <Stream sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={"body2"}>Stream Rate Amount</Typography>
                </Box>

                <Box display={"flex"} sx={{ textAlign: "right" }} flexDirection={"column"}>
                  <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                    {`$${getRateAmount(selectedRateCard.rate, "stream")}`}
                  </Typography>
                </Box>
              </Box>

              <Box display={"flex"} alignItems={"center"} sx={{ borderBottom: "1px dotted", py: 0.8 }} justifyContent={"space-between"}>
                <Box display={"flex"} alignItems={"center"} gap={0.8}>
                  <Event sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={"body2"}>Daily Rate Amount</Typography>
                </Box>

                <Box display={"flex"} sx={{ textAlign: "right" }} flexDirection={"column"}>
                  <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                    {`$${getRateAmount(selectedRateCard.rate, "daily")}`}
                  </Typography>
                </Box>
              </Box>

              <Box display={"flex"} alignItems={"center"} sx={{ borderBottom: "1px dotted", py: 0.8 }} justifyContent={"space-between"}>
                <Box display={"flex"} alignItems={"center"} gap={0.8}>
                  <ReceiptLong sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={"body2"}>Salaried Rate Amount</Typography>
                </Box>

                <Box display={"flex"} sx={{ textAlign: "right" }} flexDirection={"column"}>
                  <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                    {`$${getRateAmount(selectedRateCard.rate, "salaried")}`}
                  </Typography>
                </Box>
              </Box>

              <Box display={"flex"} alignItems={"center"} sx={{ borderBottom: "1px dotted", py: 0.8 }} justifyContent={"space-between"}>
                <Box display={"flex"} alignItems={"center"} gap={0.8}>
                  <Payment sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={"body2"}>Fixed Rate Amount</Typography>
                </Box>

                <Box display={"flex"} sx={{ textAlign: "right" }} flexDirection={"column"}>
                  <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
                    {`$${getRateAmount(selectedRateCard.rate, "fixed")}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewRateCardDialog;
